import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { BannerData } from '../interface-model/app-interface-model';


@Injectable({ providedIn: 'root' })
export class BannerService {

  private currentBanner: BannerData | null = null;
  private bannerSub = new BehaviorSubject<BannerData | null>(null);
  banner$ = this.bannerSub.asObservable();
  private countdownSubs: Map<string, Subscription> = new Map();


  show(banner: BannerData) {
    this.currentBanner = banner;
    this.bannerSub.next({ ...banner }); // emit fresh copy

    // If banner has countdown, start it (or resume)
    if (!banner.isIndefinite && typeof banner.countdownEndTime === 'number') {
      this.startCountdown(banner);
    }
  }

  clear(id?: string) {
    if (!id) {
      this.currentBanner = null;
      this.bannerSub.next(null);
      this.clearAllCountdowns();
      return;
    }
    if (this.currentBanner?.id === id) {
      this.currentBanner = null;
      this.bannerSub.next(null);
      this.clearCountdown(id);
    }
  }

  clearAll() {
    this.currentBanner = null;
    this.bannerSub.next(null);
    this.clearAllCountdowns();
  }

  handleBanner(banner: BannerData) {
    if (!banner.id) return;

    if (banner.isActive) {
      this.show({ ...banner });
    } else {
      this.clear(banner.id);
    }
  }

  handleBannerWithTimer(banner: BannerData & { rawTime?: string | null, systemMode?: string | null }) {
    if (banner.id === 'serviceMode') {
      if (banner.systemMode !== 'service') {
        this.clear(banner.id);
        return;
      }
    }
    const countdownEndTime = banner.rawTime ? this.parseUtcTime(banner.rawTime) : banner.countdownEndTime;
    const isIndefinite = banner.id === 'serviceMode' && banner.isActive && banner.systemMode === 'service' && !countdownEndTime;
    this.handleBanner({ ...banner, countdownEndTime, isIndefinite });
  }

  private startCountdown(banner: BannerData) {
    this.clearCountdown(banner.id!);

    const sub = interval(1000).subscribe(() => {
      const diff = banner.countdownEndTime! - Date.now();
      if (diff <= 0) {
        this.clear(banner.id);
        sub.unsubscribe();
        this.countdownSubs.delete(banner.id!);
        return;
      }

      // Update banner copy with countdownText
      const totalSec = Math.ceil(diff / 1000);
      const minutes = Math.floor(totalSec / 60);
      const seconds = totalSec % 60;
      const updatedBanner = { ...banner, countdownText: `${minutes} Mins ${seconds.toString().padStart(2, '0')} Sec` };
      this.currentBanner = updatedBanner;
      this.bannerSub.next(updatedBanner);
    });

    this.countdownSubs.set(banner.id!, sub);
  }

  private clearCountdown(id: string) {
    const sub = this.countdownSubs.get(id);
    sub?.unsubscribe();
    this.countdownSubs.delete(id);
  }

  private clearAllCountdowns() {
    this.countdownSubs.forEach(sub => sub.unsubscribe());
    this.countdownSubs.clear();
  }

  parseUtcTime(timeStr?: string): number | undefined {
    if (!timeStr) return undefined;
    const utcString = timeStr.endsWith('Z') ? timeStr : timeStr + 'Z'; //UTC safe, validate from different time zones
    const time = new Date(utcString).getTime();
    return isNaN(time) ? undefined : time;
  }
}

